"use client";
import { T, useTextLocale, tl } from "@/components/t";

import { useEffect } from "react";
import Link from "next/link";
import { AlertTriangle, RefreshCw, Shield, HeartPulse, BookOpen } from "lucide-react";

export default function InsuranceError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.error("[careerkit] /insurance error:", error);
  }, [error]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div className="rounded-2xl border bg-red-500/[0.04] border-red-500/20 p-5 mb-8 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-sm">
          <p className="font-semibold text-foreground"><T de={"Versicherungs-Check momentan nicht verfügbar."} en={"Insurance check unavailable right now."} /></p>
          <p className="text-muted-foreground mt-0.5">
            <T de={"Wir konnten dein interaktives Ergebnis nicht laden. Hier sind die Basics ohne Login -\n            und ein Retry-Button falls es wieder klappt."} en={"We couldn't load your interactive result. Here are the basics without logging in -\n            and a retry button in case it works again."} />
          </p>
          <button
            onClick={reset}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90"
          >
            <RefreshCw className="h-3 w-3" /> <T de="Erneut versuchen" en="Try again" />
          </button>
        </div>
      </div>

      <h1 className="text-2xl font-extrabold text-foreground tracking-tight mb-1">Versicherungs-Basics</h1>
      <p className="text-sm text-muted-foreground mb-6">
        <T de={"Was so gut wie jeder in Deutschland braucht, sortiert nach Hebel."} en={"What almost everyone in Germany needs, ranked by impact."} />
      </p>

      <div className="space-y-3 mb-8">
        <div className="rounded-2xl border-2 border-destructive/30 bg-destructive/[0.04] p-5">
          <div className="flex items-start gap-4">
            <span className="text-2xl">💪</span>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-bold text-foreground">BU-Versicherung</p>
                <span className="text-xs font-bold bg-destructive text-white px-1.5 py-0.5 rounded"><T de="HÖCHSTE PRIO" en="HIGHEST PRIORITY" /></span>
              </div>
              <p className="text-sm text-muted-foreground">
                Zahlt eine monatliche Rente, wenn du deinen Beruf aus gesundheitlichen Gründen nicht mehr ausüben kannst.
                Je früher abgeschlossen, desto günstiger, als Schüler/Student ab ~30 €/Mon.
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border bg-card p-5">
          <div className="flex items-start gap-4">
            <span className="text-2xl">🛡️</span>
            <div className="flex-1">
              <p className="font-bold text-foreground mb-1">Haftpflichtversicherung</p>
              <p className="text-sm text-muted-foreground">
                <T de={"Schützt vor Schadenersatzforderungen, wenn du anderen versehentlich Schaden zufügst -\n                Millionen-Risiko für ~5 €/Mon."} en={"Covers claims for damages when you accidentally cause harm to others -\n                a million-euro risk for ~€5/month."} />
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border bg-card p-5">
          <div className="flex items-start gap-4">
            <span className="text-2xl">🏠</span>
            <div className="flex-1">
              <p className="font-bold text-foreground mb-1">Hausratversicherung</p>
              <p className="text-sm text-muted-foreground">
                <T de={"Sobald du eine eigene Wohnung hast, schützt Möbel, Elektronik, Fahrrad bei Einbruch / Brand / Wasser.\n                Ab ~5 €/Mon."} en={"As soon as you have your own flat, it covers furniture, electronics and your bike against burglary / fire / water.\n                From ~€5/month."} />
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Link
          href="/finanzen/fix/bu"
          className="flex items-center gap-2 rounded-xl border p-3 text-sm font-medium hover:border-primary/30 hover:bg-muted/40 transition-colors"
        >
          <HeartPulse className="h-4 w-4 text-destructive" /> BU-Tiefenanalyse
        </Link>
        <Link
          href="/pkv-gkv"
          className="flex items-center gap-2 rounded-xl border p-3 text-sm font-medium hover:border-primary/30 hover:bg-muted/40 transition-colors"
        >
          <Shield className="h-4 w-4 text-primary" /> PKV vs. GKV
        </Link>
        <Link
          href="/finanzen"
          className="flex items-center gap-2 rounded-xl border p-3 text-sm font-medium hover:border-primary/30 hover:bg-muted/40 transition-colors"
        >
          <BookOpen className="h-4 w-4 text-muted-foreground" /> <T de={"Zur Finanz-Übersicht"} en={"To the finance overview"} />
        </Link>
      </div>

      {error.digest && (
        <p className="text-xs text-muted-foreground text-center mt-6">
          <T de={"Fehler-ID:"} en={"Error ID:"} /> <code className="rounded bg-muted px-1 py-0.5">{error.digest}</code>
        </p>
      )}
    </div>
  );
}
